import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import Button from './Button';
import css from './DropdownButton.module.scss';
import ChevronUp from '../Icons/ChevronUp.jsx';
import ChevronDown from '../Icons/ChevronDown.jsx';

const DropdownButton = ({
  children,
  label,
  icon,
  position = 'below',
  align = 'right',
  disabled = false,
  className = '',
  variant,
  size,
  style,
  showExpandableArrows = true,
  menuInPortal = false,
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [portalCoords, setPortalCoords] = useState(null);
  const wrapperRef = useRef(null);
  const menuRef = useRef(null);

  // Close dropdown when clicking outside (accounting for a portaled menu that
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!isOpen) {
        return;
      }
      const inWrapper = wrapperRef.current && wrapperRef.current.contains(event.target);
      const inMenu = menuRef.current && menuRef.current.contains(event.target);
      if (!inWrapper && !inMenu) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !menuInPortal) {
      return;
    }
    const handleViewportChange = () => {
      setIsOpen(false);
    };
    window.addEventListener('scroll', handleViewportChange, true);
    window.addEventListener('resize', handleViewportChange);
    return () => {
      window.removeEventListener('scroll', handleViewportChange, true);
      window.removeEventListener('resize', handleViewportChange);
    };
  }, [isOpen, menuInPortal]);

  const dropdownClasses = [
    css.dropdown,
    position === 'above' ? css.dropdownAbove : css.dropdownBelow,
    align === 'left' ? css.dropdownAlignLeft : css.dropdownAlignRight
  ].filter(Boolean).join(' ');

  const computePortalCoords = () => {
    const rect = wrapperRef.current?.getBoundingClientRect();
    if (!rect) {
      return null;
    }
    return {
      position: 'fixed',
      top: position === 'above' ? undefined : rect.bottom + 4,
      bottom: position === 'above' ? (window.innerHeight - rect.top + 4) : undefined,
      left: align === 'left' ? rect.left : undefined,
      right: align === 'right' ? (window.innerWidth - rect.right) : undefined,
    };
  };

  const onClick = (e) => {
    e.preventDefault();
    setIsOpen((prev) => {
      const next = !prev;
      if (next && menuInPortal) {
        setPortalCoords(computePortalCoords());
      }
      return next;
    });
  }

  const onChildClick = () => {
    setIsOpen(false); // Close the dropdown when a child is clicked
  };


  return (
    <div className={css.dropdownWrapper} ref={wrapperRef} style={style}>
      <Button
        {...props}
        disabled={disabled}
        className={className}
        onClick={onClick}
        icon={icon}
        iconPosition="left"
        variant={variant}
        size={size}
      >
        {showExpandableArrows &&
          <span className={css.content}>
            {label}
            <span className={`${css.dropdownArrow} ${size}`}>
              {position === 'above' ? <ChevronUp width="15px"/> : <ChevronDown width="15px"/>}
            </span>
          </span>
        }
      </Button>

      {isOpen && !menuInPortal &&
        <div className={dropdownClasses} onClick={onChildClick}>
          {children}
        </div>
      }
      {isOpen && menuInPortal && portalCoords && createPortal(
        <div ref={menuRef} className={css.portalMenu} style={portalCoords} onClick={onChildClick}>
          {children}
        </div>,
        document.body
      )}
    </div>
  );
};

DropdownButton.propTypes = {
  children: PropTypes.node.isRequired,
  label: PropTypes.node.isRequired,
  icon: PropTypes.node,
  position: PropTypes.oneOf(['above', 'below']),
  align: PropTypes.oneOf(['left', 'right']),
  disabled: PropTypes.bool,
  className: PropTypes.string,
  variant: PropTypes.oneOf([
    'primary',
    'secondary',
    'tertiary',
    'link',
    'success',
    'warning',
    'danger'
  ]),
  size: PropTypes.oneOf(['small', 'default']),
  menuInPortal: PropTypes.bool,
};

export default DropdownButton;
